import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAnnualSummary, downloadAnnualCSV, getClients } from '../services/api';
import { Download, FileText, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const curYear = new Date().getFullYear();
const YEARS   = [curYear - 2, curYear - 1, curYear];
const FMT     = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const FMTN    = (n) => Number(n || 0).toLocaleString('en-IN');

const TH = ({ children, right }) => (
  <th className={`whitespace-nowrap text-xs font-medium text-gray-400 px-3 py-3 sticky top-0 bg-gray-50 ${right ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);

const AnnualPayroll = () => {
  const [year,     setYear]     = useState(curYear);
  const [clientId, setClientId] = useState('');
  const [loading,  setLoading]  = useState(false);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 200 }).then(r => r.data.data || [])
  });

  const params = { year, ...(clientId ? { client_id: clientId } : {}) };

  const { data, isLoading } = useQuery({
    queryKey: ['annual-summary', year, clientId],
    queryFn : () => getAnnualSummary(params).then(r => r.data.data)
  });

  const records = data?.records || [];

  const totals = records.reduce((acc, r) => {
    acc.ytd_gross        += r.ytd_gross;
    acc.ytd_basic        += r.ytd_basic;
    acc.ytd_pf_emp       += r.ytd_pf_emp;
    acc.ytd_pf_employer  += r.ytd_pf_employer;
    acc.ytd_esi_emp      += r.ytd_esi_emp;
    acc.ytd_esi_employer += r.ytd_esi_employer;
    acc.ytd_pt           += r.ytd_pt;
    acc.ytd_tds          += r.ytd_tds;
    acc.ytd_net          += r.ytd_net;
    return acc;
  }, { ytd_gross: 0, ytd_basic: 0, ytd_pf_emp: 0, ytd_pf_employer: 0, ytd_esi_emp: 0, ytd_esi_employer: 0, ytd_pt: 0, ytd_tds: 0, ytd_net: 0 });

  const handleDownload = async () => {
    setLoading(true);
    try {
      const res  = await downloadAnnualCSV(params);
      const name = `Annual_Payroll_Summary_${year}.csv`;
      const url  = URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement('a'); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(name + ' downloaded');
    } catch { toast.error('Download failed'); }
    finally   { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Annual Payroll Summary</h1>
          <p className="text-sm text-gray-400 mt-0.5">Year-to-date earnings, deductions and statutory for Form 16 / annual returns</p>
        </div>
        <button onClick={handleDownload} disabled={loading || !records.length}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Download size={15} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select value={year} onChange={e => setYear(+e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={clientId} onChange={e => setClientId(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
      </div>

      {/* Info banner */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <FileText size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-700">
          <strong>Form 16 / Annual Statement</strong> — This shows year-to-date salary totals per employee.
          Export the CSV and use it to prepare Form 16 Part B (salary income details) and annual PF/ESI returns.
          Only processed/approved/paid payroll months are included.
        </div>
      </div>

      {/* Summary tiles */}
      {records.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Employees Covered',   v: records.length, unit: '', c: 'text-blue-600'   },
            { label: 'YTD Gross Salary',     v: totals.ytd_gross, c: 'text-gray-900'    },
            { label: 'YTD PF (Both sides)',  v: totals.ytd_pf_emp + totals.ytd_pf_employer, c: 'text-red-600' },
            { label: 'YTD Net Salary',       v: totals.ytd_net,   c: 'text-green-700'   }
          ].map(({ label, v, c, unit }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-400 mb-1">{label}</p>
              <p className={`text-xl font-bold ${c}`}>{unit !== undefined && unit === '' ? v : `₹${FMTN(Math.round(v))}`}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-auto" style={{ maxHeight: '55vh' }}>
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading annual summary...</div>
        ) : !records.length ? (
          <div className="text-center py-16">
            <AlertCircle size={28} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">No payroll data for {year}.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100">
              <tr>
                <TH>Employee</TH>
                <TH>PAN / UAN</TH>
                <TH>Client</TH>
                <TH>Months</TH>
                <TH right>YTD Gross</TH>
                <TH right>YTD Basic</TH>
                <TH right>YTD HRA</TH>
                <TH right>PF (Emp)</TH>
                <TH right>PF (Emplr)</TH>
                <TH right>ESI (Emp)</TH>
                <TH right>ESI (Emplr)</TH>
                <TH right>PT</TH>
                <TH right>TDS</TH>
                <TH right>Net Salary</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {records.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-3 min-w-40">
                    <p className="font-medium text-gray-900">{r.employee_name}</p>
                    <p className="text-xs text-gray-400">{r.employee_code}</p>
                  </td>
                  <td className="px-3 py-3">
                    <p className="text-xs font-mono text-gray-600">{r.pan || '—'}</p>
                    <p className="text-xs font-mono text-gray-400">{r.uan || '—'}</p>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 min-w-32">{r.client}</td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">{r.months_covered}</td>
                  <td className="px-3 py-3 text-right font-semibold text-gray-900">{FMT(r.ytd_gross)}</td>
                  <td className="px-3 py-3 text-right text-gray-700">{FMT(r.ytd_basic)}</td>
                  <td className="px-3 py-3 text-right text-gray-700">{FMT(r.ytd_hra)}</td>
                  <td className="px-3 py-3 text-right text-red-600">{FMT(r.ytd_pf_emp)}</td>
                  <td className="px-3 py-3 text-right text-orange-600">{FMT(r.ytd_pf_employer)}</td>
                  <td className="px-3 py-3 text-right text-red-600">{FMT(r.ytd_esi_emp)}</td>
                  <td className="px-3 py-3 text-right text-orange-600">{FMT(r.ytd_esi_employer)}</td>
                  <td className="px-3 py-3 text-right text-red-600">{FMT(r.ytd_pt)}</td>
                  <td className="px-3 py-3 text-right text-red-600">{FMT(r.ytd_tds)}</td>
                  <td className="px-3 py-3 text-right font-bold text-green-700">{FMT(r.ytd_net)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-gray-300 bg-gray-50 sticky bottom-0">
              <tr>
                <td className="px-3 py-3 font-semibold text-sm text-gray-700" colSpan={4}>Total ({records.length})</td>
                <td className="px-3 py-3 text-right font-semibold text-gray-900">{FMT(totals.ytd_gross)}</td>
                <td className="px-3 py-3 text-right font-semibold text-gray-700">{FMT(totals.ytd_basic)}</td>
                <td className="px-3 py-3 text-right text-gray-700">—</td>
                <td className="px-3 py-3 text-right font-semibold text-red-600">{FMT(totals.ytd_pf_emp)}</td>
                <td className="px-3 py-3 text-right font-semibold text-orange-600">{FMT(totals.ytd_pf_employer)}</td>
                <td className="px-3 py-3 text-right font-semibold text-red-600">{FMT(totals.ytd_esi_emp)}</td>
                <td className="px-3 py-3 text-right font-semibold text-orange-600">{FMT(totals.ytd_esi_employer)}</td>
                <td className="px-3 py-3 text-right font-semibold text-red-600">{FMT(totals.ytd_pt)}</td>
                <td className="px-3 py-3 text-right font-semibold text-red-600">{FMT(totals.ytd_tds)}</td>
                <td className="px-3 py-3 text-right font-bold text-green-700">{FMT(totals.ytd_net)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};

export default AnnualPayroll;
